// src/pages/admin/AdminPayments.tsx
// Route: /admin/payments

import { useState, useMemo } from "react";
import {
  useAdminPreviewPaymentDueQuery,
  useAdminSendPaymentDueRemindersMutation,
} from "../../app/api/progressReportApi";
import type { PayStatus } from "../../app/api/parentApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  StatCard,
  TableContainer,
  Th,
  Td,
  Pagination,
  SearchInput,
  Select,
  Button,
  PayChip,
  formatNaira,
  formatDate,
} from "./AdminShared";
import {
  AdminPayment,
  useAdminConfirmPaymentMutation,
  useAdminGetPaymentsQuery,
  useAdminRefundPaymentMutation,
  useAdminDecideRefundMutation,
} from "../../app/api/adminApi";

import { Check, CreditCard, Hourglass, Mail, Users } from "lucide-react";
/* ─── CONFIRM PAYMENT MODAL ──────────────────────────────────────── */

function ConfirmPaymentModal({
  enrolmentId,
  onClose,
  onDone,
}: {
  enrolmentId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [confirm, { isLoading }] = useAdminConfirmPaymentMutation();
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!reference.trim()) {
      setError("Reference is required");
      return;
    }
    setError("");
    try {
      await confirm({
        enrolmentId,
        reference: reference.trim(),
        notes: notes.trim() || undefined,
      }).unwrap();
      onDone();
    } catch (err: any) {
      setError(err?.data?.error ?? "Confirmation failed");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-[#16213e]/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-[24px] bg-white p-6 shadow-[0_24px_80px_rgba(27,45,94,0.20)]">
          <h3 className="text-lg font-bold text-[#16213e]">Confirm payment</h3>
          <p className="mt-1 text-xs text-[#8189a0]">
            Manually confirm a bank transfer. This will activate the enrolment.
          </p>
          <div className="mt-5 space-y-3">
            <div>
              <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Bank reference *
              </label>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. TRF-2026XXXX"
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              />
            </div>
            <div>
              <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Notes
              </label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional"
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              />
            </div>
            {error && <p className="text-xs text-[#e57373]">{error}</p>}
          </div>
          <div className="mt-5 flex gap-2.5">
            <Button variant="secondary" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Confirming…" : "Confirm payment"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── 14-DAY MONEY-BACK REQUEST ──────────────────────────────────── */

function MoneyBackRequest({ payment, onDone }: { payment: AdminPayment; onDone: () => void }) {
  const r = payment.refundRequest;
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [decide, { isLoading }] = useAdminDecideRefundMutation();
  if (!r) return null;
  const when = (iso: string | null) => (iso ? formatDate(iso) : "");

  const send = async (decision: "approve" | "decline") => {
    setMsg(null);
    if (decision === "approve" && !window.confirm(`Refund ${payment.amountFormatted} in full${payment.method === "paystack" ? " through Paystack" : ""}? This ends the enrolment it paid for.`)) return;
    try {
      const res = await decide({ id: payment.id, decision, note: note.trim() || undefined }).unwrap();
      setMsg({ ok: true, text: res.message ?? "Done" });
      setTimeout(onDone, 1200);
    } catch (err) {
      const e = err as { data?: { error?: string } };
      setMsg({ ok: false, text: e?.data?.error ?? "Could not save the decision" });
    }
  };

  return (
    <div className={`rounded-[18px] border p-4 text-xs ${r.status === "requested" ? "border-[#f5d9a0] bg-[#fff8ee]" : "border-[#dde2ec] bg-[#f9fafc]"}`}>
      <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#7a4a10]">14-day money-back guarantee</p>
      <p className="mt-1 text-sm text-[#16213e]">
        Requested {when(r.requestedAt)}
        {r.status !== "requested" && ` · ${r.status === "approved" ? "approved" : "declined"} ${when(r.decidedAt)}${r.decidedByName ? ` by ${r.decidedByName}` : ""}`}
      </p>
      {r.reason && <p className="mt-2 leading-5 text-[#3b4560]"><b>Parent's reason:</b> {r.reason}</p>}
      {r.note && <p className="mt-1 leading-5 text-[#3b4560]"><b>Note to parent:</b> {r.note}</p>}
      {r.status === "requested" && !msg?.ok && (
        <div className="mt-3 space-y-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Note to the parent (needed to decline; optional to approve)"
            className="w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => send("approve")} disabled={isLoading}>Approve and refund</Button>
            <Button size="sm" variant="secondary" onClick={() => send("decline")} disabled={isLoading}>Decline</Button>
          </div>
          <p className="leading-4 text-[#8189a0]">
            {payment.method === "paystack" && payment.reference
              ? "Approving refunds the full amount through Paystack; the payment shows as refunded when Paystack confirms."
              : "This payment didn't come through Paystack: refund it the way it was paid, then approve here to record it."}
          </p>
        </div>
      )}
      {msg && <p className={`mt-2 font-semibold ${msg.ok ? "text-[#2a7a56]" : "text-[#b94a3a]"}`}>{msg.text}</p>}
    </div>
  );
}

/* ─── PAYMENT DETAIL DRAWER ──────────────────────────────────────── */

function PaymentDetailDrawer({
  payment,
  onClose,
}: {
  payment: AdminPayment;
  onClose: () => void;
}) {
  const [refund, { isLoading: refunding }] = useAdminRefundPaymentMutation();
  const [refundAmt, setRefundAmt] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");

  const handleRefund = async () => {
    if (!window.confirm("Initiate Paystack refund for this payment?")) return;
    setError("");
    try {
      await refund({
        id: payment.id,
        amountNaira: refundAmt ? Number(refundAmt) : undefined,
        reason: refundNote || undefined,
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err?.data?.error ?? "Refund failed");
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[460px]"
        role="dialog"
        aria-modal
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-[#16213e]">Payment detail</h2>
            <p className="mt-0.5 font-mono text-[12.5px] text-[#8189a0]">
              {payment.reference ?? "No reference"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Amount hero */}
          <div className="rounded-[20px] bg-[#f9fafc] border border-[#dde2ec] p-5 text-center">
            <PayChip status={payment.status} />
            <p className="mt-3 text-4xl font-bold tracking-[-0.04em] text-[#16213e]">
              {payment.amountFormatted}
            </p>
            <p className="mt-1 text-xs text-[#8189a0]">{payment.description}</p>
          </div>

          <MoneyBackRequest payment={payment} onDone={onClose} />

          {/* Details */}
          <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
            <div className="bg-[#f9fafc] px-4 py-2.5 border-b border-[#ebeff7]">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Details
              </p>
            </div>
            <div className="divide-y divide-[#f7f8fb] text-xs">
              {[
                {
                  label: "Student",
                  value: payment.child
                    ? `${payment.child.firstName} ${payment.child.lastName}`
                    : "—",
                },
                {
                  label: "Parent",
                  value: payment.parent
                    ? `${payment.parent.firstName} ${payment.parent.lastName}`
                    : "—",
                },
                { label: "Email", value: payment.parent?.email ?? "—" },
                { label: "Method", value: payment.method ?? "—" },
                { label: "Paid on", value: formatDate(payment.paidAt) },
                { label: "Created", value: formatDate(payment.createdAt) },
              ].map((r) => (
                <div key={r.label} className="flex justify-between px-4 py-2.5">
                  <span className="text-[#8189a0]">{r.label}</span>
                  <span className="font-semibold text-[#16213e] text-right max-w-[200px] truncate">
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Gateway response */}
          {payment.gatewayResponse && (
            <div className="rounded-[16px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Gateway
              </p>
              <p className="mt-1 text-xs text-[#5a6278]">
                {payment.gatewayResponse}
              </p>
            </div>
          )}

          {/* Refund section */}
          {payment.status === "paid" && (
            <div className="rounded-[18px] border border-[#f5c0c0] bg-[#fff5f5] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#f5c0c0]">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#9b2a2a]">
                  Initiate Paystack refund
                </p>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-[12.5px] font-semibold text-[#8189a0]">
                    Partial refund amount (₦)
                  </label>
                  <input
                    type="number"
                    value={refundAmt}
                    onChange={(e) => setRefundAmt(e.target.value)}
                    placeholder="Leave blank for full refund"
                    className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
                  />
                </div>
                <div>
                  <label className="text-[12.5px] font-semibold text-[#8189a0]">
                    Reason
                  </label>
                  <input
                    value={refundNote}
                    onChange={(e) => setRefundNote(e.target.value)}
                    placeholder="Optional"
                    className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
                  />
                </div>
                {error && <p className="text-xs text-[#e57373]">{error}</p>}
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={handleRefund}
                  disabled={refunding}
                >
                  {refunding ? "Processing…" : "Initiate refund via Paystack"}
                </Button>
              </div>
            </div>
          )}

          {/* Manual confirm for pending */}
          {payment.status === "pending" && payment.enrolment && (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => setShowConfirm(true)}
            >
              <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Manually confirm bank transfer
            </Button>
          )}
        </div>
      </div>

      {showConfirm && payment.enrolment && (
        <ConfirmPaymentModal
          enrolmentId={payment.enrolment}
          onClose={() => setShowConfirm(false)}
          onDone={() => {
            setShowConfirm(false);
            onClose();
          }}
        />
      )}
    </>
  );
}

/* ─── PAYMENT DUE REMINDER PANEL ─────────────────────────────────── */

function PaymentDuePanel() {
  const [daysAhead, setDaysAhead] = useState(7);
  const [sendReminders, { isLoading: sending }] =
    useAdminSendPaymentDueRemindersMutation();
  const [sent, setSent] = useState<{ sent: number; failed: number } | null>(
    null,
  );
  const [sendError, setSendError] = useState("");

  const { data, isLoading } = useAdminPreviewPaymentDueQuery({
    daysAhead,
  });
  const due = data?.data ?? [];

  const handleSend = async () => {
    if (!window.confirm(`Send payment reminder to ${due.length} parent(s)?`))
      return;
    setSendError("");
    setSent(null);
    try {
      const result = await sendReminders({ daysAhead }).unwrap();
      setSent({ sent: result.sent, failed: result.failed });
    } catch (err: any) {
      setSendError(err?.data?.error ?? "Failed to send");
    }
  };

  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-6 shadow-[0_4px_16px_rgba(27,45,94,0.05)]">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-base font-bold text-[#16213e]">
            Payment due reminders
          </h3>
          <p className="mt-0.5 text-xs text-[#5f6780]">
            Find parents whose monthly or quarterly renewal is approaching and
            send them an email reminder.
          </p>
        </div>
        <select
          value={daysAhead}
          onChange={(e) => setDaysAhead(Number(e.target.value))}
          className="rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold outline-none focus:border-[#1b2d5e]"
        >
          <option value={3}>Next 3 days</option>
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
          <option value={30}>Next 30 days</option>
        </select>
      </div>

      {isLoading ? (
        <Skeleton className="h-20 rounded-[16px]" />
      ) : due.length === 0 ? (
        <div className="rounded-[16px] bg-[#edfaf4] border border-[#b6e8d0] px-4 py-4 text-center">
          <p className="text-sm font-semibold text-[#2a7a56]">
            <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}No payments due in the next {daysAhead} days
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-[16px] border border-[#f5d9a0] bg-[#fff8ee] mb-4 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#f5d9a0] flex items-center justify-between">
              <p className="text-xs font-bold text-[#7a4a10]">
                {due.length} payment{due.length > 1 ? "s" : ""} due within{" "}
                {daysAhead} days
              </p>
              <p className="text-[11.5px] text-[#8189a0]">click to preview</p>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-[#fef0d4]">
              {due.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-4 py-2.5 text-xs"
                >
                  <div>
                    <p className="font-semibold text-[#16213e]">
                      {item.childFirstName} {item.childLastName}
                    </p>
                    <p className="text-[#8189a0]">{item.parentEmail}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#7a4a10]">
                      {formatNaira(item.feeAmount)}
                    </p>
                    <p className="text-[#8189a0]">Due {item.nextDueLabel}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {sent ? (
            <div className="rounded-[16px] bg-[#edfaf4] border border-[#b6e8d0] px-4 py-3 text-center">
              <p className="text-sm font-bold text-[#2a7a56]">
                <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{sent.sent} reminder{sent.sent !== 1 ? "s" : ""} sent
              </p>
              {sent.failed > 0 && (
                <p className="text-xs text-[#e57373] mt-0.5">
                  {sent.failed} failed
                </p>
              )}
            </div>
          ) : (
            <>
              {sendError && (
                <p className="mb-3 text-xs text-[#e57373]">{sendError}</p>
              )}
              <Button
                variant="primary"
                className="w-full"
                onClick={handleSend}
                disabled={sending}
              >
                {sending
                  ? "Sending…"
                  : <><Mail className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> {`Send ${due.length} reminder email${due.length > 1 ? "s" : ""}`}</>}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ─── TABLE ROW ──────────────────────────────────────────────────── */

function PaymentRow({
  payment,
  onClick,
}: {
  payment: AdminPayment;
  onClick: () => void;
}) {
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-[#f7f8fb] transition hover:bg-[#f9fafc]"
    >
      <Td>
        <div>
          <p className="font-semibold text-[#16213e]">{payment.description}</p>
          {payment.refundRequest?.status === "requested" && (
            <span className="mt-0.5 inline-block rounded-full bg-[#fff8ee] px-2 py-0.5 text-[11px] font-bold text-[#7a4a10]">Refund requested</span>
          )}
          {payment.child && (
            <p className="text-[12.5px] text-[#8189a0]">
              {payment.child.firstName} {payment.child.lastName}
            </p>
          )}
        </div>
      </Td>
      <Td>
        <p className="font-bold text-[#16213e]">{payment.amountFormatted}</p>
      </Td>
      <Td>
        {payment.parent && (
          <div>
            <p className="text-xs font-medium text-[#16213e]">
              {payment.parent.firstName} {payment.parent.lastName}
            </p>
            <p className="text-[12.5px] text-[#8189a0]">{payment.parent.email}</p>
          </div>
        )}
      </Td>
      <Td>
        <PayChip status={payment.status} />
      </Td>
      <Td>
        <p className="text-xs text-[#5a6278]">
          {formatDate(payment.paidAt ?? payment.createdAt)}
        </p>
      </Td>
      <Td>
        <p className="max-w-[120px] truncate font-mono text-[11.5px] text-[#a6adbf]">
          {payment.reference ?? "—"}
        </p>
      </Td>
      <Td>
        <svg className="h-4 w-4 text-[#c9cfe0]" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Td>
    </tr>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function AdminPayments() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminPayment | null>(null);

  const { data, isLoading, isError, refetch } = useAdminGetPaymentsQuery({
    page,
    limit: 25,
    ...(statusFilter === "refund_requested"
      ? { refund: "requested" as const }
      : statusFilter
        ? { status: statusFilter as PayStatus }
        : {}),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const payments = data?.data ?? [];
  const summary = data?.summary;

  const filtered = useMemo(() => {
    if (!search.trim()) return payments;
    const q = search.toLowerCase();
    return payments.filter(
      (p: any) =>
        p.description.toLowerCase().includes(q) ||
        p.reference?.toLowerCase().includes(q) ||
        (p.child &&
          `${p.child.firstName} ${p.child.lastName}`
            .toLowerCase()
            .includes(q)) ||
        (p.parent &&
          `${p.parent.firstName} ${p.parent.lastName}`
            .toLowerCase()
            .includes(q)),
    );
  }, [payments, search]);

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <p className="text-sm font-semibold text-[#16213e]">
          Failed to load payments
        </p>
        <Button variant="primary" onClick={refetch}>
          Retry
        </Button>
      </div>
    );

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle="All payment records. Confirm bank transfers, issue refunds, and send payment reminders."
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] rounded-[20px]" />
            ))
          : [
              {
                label: "Total received",
                value: summary ? formatNaira(summary.totalPaid) : "—",
                icon: Check,
                color: "#6cc2a0",
                bg: "#edfaf4",
                sub: "paid",
              },
              {
                label: "Pending",
                value: summary ? formatNaira(summary.totalPending) : "—",
                icon: Hourglass,
                color: "#f0a050",
                bg: "#fff8ee",
                sub: "awaiting",
              },
              {
                label: "Total records",
                value: String(data?.total ?? 0),
                icon: CreditCard,
                color: "#1b2d5e",
                bg: "#ebeff7",
                sub: "all time",
              },
              {
                label: "This page",
                value: String(filtered.length),
                icon: Users,
                color: "#4a44b0",
                bg: "#e8ecf5",
                sub: "shown",
              },
            ].map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Two-column layout: table + reminder panel */}
      <div className="grid gap-6 xl:grid-cols-3">
        {/* Table */}
        <div className="xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
              placeholder="Search by name, reference…"
            />
            <Select
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="overdue">Overdue</option>
              <option value="refund_requested">Money-back requests</option>
            </Select>
            <p className="text-xs text-[#8189a0]">{filtered.length} records</p>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-xl" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No payments found"
              subtitle="Try adjusting your filters."
            />
          ) : (
            <TableContainer>
              <table className="w-full text-left text-sm">
                <thead className="border-b border-[#ebeff7]">
                  <tr>
                    {[
                      "Description",
                      "Amount",
                      "Parent",
                      "Status",
                      "Date",
                      "Reference",
                      "",
                    ].map((h) => (
                      <Th key={h}>{h}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p: any) => (
                    <PaymentRow
                      key={p.id}
                      payment={p}
                      onClick={() => setSelected(p)}
                    />
                  ))}
                </tbody>
              </table>
              <Pagination
                page={data?.page ?? 1}
                pages={data?.pages ?? 1}
                total={data?.total ?? 0}
                count={filtered.length}
                onPage={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0 });
                }}
              />
            </TableContainer>
          )}
        </div>

        {/* Reminder panel */}
        <div>
          <PaymentDuePanel />
        </div>
      </div>

      {selected && (
        <PaymentDetailDrawer
          payment={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
